import { useEffect, useRef, useState } from "react";
import { AcceleratorBatch } from "../../models/AcceleratorBatch";
import DocTypeConfig from "../../models/config/DocTypeConfig";
import { BatchSearchOption } from "../../models/search/BatchSearchOption";
import { getDocumentStatuses } from "../../types/DocumentStatuses";
import { Search } from "../search/Search";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import { TTBatchInstanceSearch } from "../tooltips/batchListPanel/TTBatchInstanceSearch";
import { TTSearchBatch } from "../tooltips/ConfigurationTooltips";

export { BatchInstanceSearch };

function createSearchOptions(
  docTypes: DocTypeConfig[],
  batches: AcceleratorBatch[],
  cachedData: BatchSearchOption[],
): BatchSearchOption[] {
  const newData: BatchSearchOption[] = [];
  for (let i = 0; i < batches.length; ++i) {
    const accBatch = batches[i];
    let cached;
    if (
      (cached = cachedData.find((cd) => cd.key === accBatch.uuid)) != undefined
    ) {
      newData.push(cached);
    } else {
      newData.push({
        key: accBatch.uuid,
        batchInstanceId: accBatch.batchInstanceId,
        batchInstanceName: accBatch.batchInstanceName,
        docTypes: accBatch.metadata
          .map((d) => d.classifiedDocType)
          .map((x) =>
            docTypes.find((d) => d.typeName === x)?.description.toLowerCase(),
          )
          .filter((x): x is string => x != undefined)
          .filter((x, i, arr) => arr.indexOf(x) === i),
        modelNames: accBatch.metadata
          .map((d) => d.getModelName().toLowerCase())
          .filter((x, i, arr) => arr.indexOf(x) === i),
        pages: accBatch.metadata
          .map((d) => `${d.pageCount} Pages`.toLowerCase())
          .filter((x, i, arr) => arr.indexOf(x) === i),
        statuses: accBatch.metadata
          .flatMap((d) => d.statuses)
          .filter((x, i, arr) => arr.indexOf(x) === i),
      });
    }
  }

  return newData;
}

const rePage = /\d+ Pages?/i;
const reBatchId = /BI[\d\w]+/i;
const reDocStatuses = new RegExp(getDocumentStatuses().join("|"), "gi");

type Props = {
  allAcceleratorBatches: AcceleratorBatch[];
  docTypes: DocTypeConfig[];
  defaultSearchTerm: string;
  onSearch: (uuids: string[] | null, filter: string) => void;
};

const BatchInstanceSearch = (props: Props) => {
  // const docTypesRef = useRef<string[]>(props.docTypes.map);
  const modelNamesRef = useRef<string[]>([]);

  // const [searchOptions, setSearchOptions] = useState(() => createSearchOptions(props.docTypes, props.allAcceleratorBatches, []));
  const [searchOptions, setSearchOptions] = useState<BatchSearchOption[]>([]);

  useEffect(() => {
    // FIXME: invalidate cache using accbatch version
    setSearchOptions(
      createSearchOptions(
        props.docTypes,
        props.allAcceleratorBatches,
        searchOptions,
      ),
    );

    modelNamesRef.current = props.allAcceleratorBatches
      .flatMap((b) => b.metadata)
      .map((d) => d.classifiedModelName)
      .filter((x, i, arr) => arr.indexOf(x) === i);
  }, [props.allAcceleratorBatches, props.docTypes]);

  const searchBatches = async (filter: string): Promise<string[]> => {
    const searchResults: typeof searchOptions = [];

    filter = filter.toLowerCase();
    // loop over search options until a matching column is found, then search remaining items on that column
    for (let i = 0; i < searchOptions.length; ++i) {
      const so = searchOptions[i];

      if (so.batchInstanceId.toLowerCase().toLowerCase().includes(filter)) {
        console.debug(
          `Added ${so.key} because BatchInstanceId=${so.batchInstanceId} matched`,
        );
        searchResults.push(so);
        searchResults.push(
          ...searchOptions
            .slice(i)
            .filter((x) => x.batchInstanceId.includes(filter)),
        );
        break;
      }

      if (so.batchInstanceName.toLowerCase().toLowerCase().includes(filter)) {
        console.debug(
          `Added ${so.key} because BatchInstanceName=${so.batchInstanceName} matched`,
        );
        searchResults.push(so);
        searchResults.push(
          ...searchOptions
            .slice(i)
            .filter((x) => x.batchInstanceName.includes(filter)),
        );
        break;
      }

      if (so.pages.find((x) => x.toLowerCase().includes(filter))) {
        console.debug(
          `Added ${so.key} because Pages=[${so.pages.join(",")}] matched`,
        );
        searchResults.push(so);
        searchResults.push(
          ...searchOptions
            .slice(i)
            .filter((x) =>
              x.pages.find((p) => p.toLowerCase().includes(filter)),
            ),
        );
        break;
      }

      if (so.docTypes.find((x) => x.toLowerCase().includes(filter))) {
        console.debug(
          `Added ${so.key} because DocType=[${so.docTypes.join(",")}] matched`,
        );
        searchResults.push(so);
        searchResults.push(
          ...searchOptions
            .slice(i)
            .filter((x) =>
              x.docTypes.find((p) => p.toLowerCase().includes(filter)),
            ),
        );
        break;
      }

      if (so.modelNames.find((x) => x.includes(filter))) {
        console.debug(
          `Added ${so.key} because ModelName=[${so.modelNames.join(",")}] matched`,
        );
        searchResults.push(so);
        searchResults.push(
          ...searchOptions
            .slice(i)
            .filter((x) =>
              x.modelNames.find((p) => p.toLowerCase().includes(filter)),
            ),
        );
        break;
      }
    }

    return searchResults.map((x) => x.key);
  };

  return (
    <AdvancedTooltip
      overflow
      tooltipText={TTSearchBatch}
      possiblePlacements={["left-center", "bottom-start"]}
    >
      {(tProps) => (
        <Search<string[]>
          tProps={tProps}
          placeholder={"Search Batches..."}
          defaultFilter={props.defaultSearchTerm}
          searchOnMount={props.defaultSearchTerm.length > 0}
          searchMethod={searchBatches}
          onSearch={props.onSearch}
        />
      )}
    </AdvancedTooltip>
  );
};
